import { useNavigate, useParams } from '@tanstack/react-router';
import { ChangeEvent, useState } from 'react';

import { useAuth } from '@/hooks/use-auth';
import { resetPasswordWithToken } from '@/lib/auth/auth-service';

import ConfirmationMessage from '../components/common/ConfirmationMessage';
import type { ErrorMessage } from '../helpers/type';
import { passwordChecker } from '../helpers/utils';
import { useTranslation } from '../hooks/useTranslation';
import ResetPasswordForm from './ResetPasswordForm';

interface ResetPasswordInput {
  password: string;
  confirmPassword: string;
  inputError: {
    password: ErrorMessage;
    confirmPassword: ErrorMessage;
  };
  border: {
    password: string;
    confirmPassword: string;
  };
}

function createInitialResetPasswordInput(): ResetPasswordInput {
  return {
    password: '',
    confirmPassword: '',
    inputError: {
      password: null,
      confirmPassword: null,
    },
    /* eslint-disable sonarjs/no-hardcoded-passwords -- CSS class names, not real passwords */
    border: {
      password: 'border-gray-300',
      confirmPassword: 'border-gray-300',
    },
    /* eslint-enable sonarjs/no-hardcoded-passwords */
  };
}

const ResetPassword = () => {
  const { t } = useTranslation();
  const { isLoading } = useAuth();
  const [loading, setLoading] = useState(isLoading);
  const [isSuccess, setIsSuccess] = useState(false);
  const navigate = useNavigate();
  const { token } = useParams({ strict: false });

  const [resetInput, setResetInput] = useState<ResetPasswordInput>(
    createInitialResetPasswordInput
  );

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setResetInput((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async () => {
    const password = passwordChecker(resetInput.password);

    // Validate password
    if (!password.isValid) {
      setResetInput((prev) => ({
        ...prev,
        inputError: {
          ...prev.inputError,
          password: password.message,
        },
        border: {
          ...prev.border,
          password: 'border-red-500',
        },
      }));
      return;
    }

    // Validate password confirmation
    if (resetInput.password !== resetInput.confirmPassword) {
      setResetInput((prev) => ({
        ...prev,
        inputError: {
          password: 'Password did not match',
          confirmPassword: 'Password did not match',
        },
        border: {
          password: 'border-red-500',
          confirmPassword: 'border-red-500',
        },
      }));
      return;
    }

    if (!token) {
      navigate({ to: '/login' });
      return;
    }

    setLoading(true);
    const result = await resetPasswordWithToken(token, resetInput.password);

    if (!result.success) {
      setResetInput((prev) => ({
        ...prev,
        inputError: {
          ...prev.inputError,
          password: 'An error occurred during sign in',
        },
      }));
      setLoading(false);
      return;
    }

    setIsSuccess(true);
    setLoading(false);
  };

  return !isSuccess ? (
    <ResetPasswordForm
      password={resetInput.password}
      confirmPassword={resetInput.confirmPassword}
      passwordError={resetInput.inputError.password}
      confirmPasswordError={resetInput.inputError.confirmPassword}
      passwordBorder={resetInput.border.password}
      confirmPasswordBorder={resetInput.border.confirmPassword}
      loading={loading}
      onInputChange={onInputChange}
      onSubmit={handleSubmit}
      title={t('resetPassword')}
      subtitle={t('pleaseEnterNewPassword')}
      passwordLabel={t('password')}
      confirmPasswordLabel={t('signup.confirmPassword')}
      submitLabel={t('resetPassword')}
    />
  ) : (
    <ConfirmationMessage
      title={t('passwordResetSuccess')}
      message={t('passwordResetMessage')}
      redirect="/login"
      buttonLabel={t('signIn')}
    />
  );
};

export default ResetPassword;
